import React from 'react';
import { useTranslation } from 'react-i18next';
import { PiCheckCircleLight, PiWarningCircleLight } from 'react-icons/pi';
import BrandLoader from './BrandLoader';
import Button from '../TailwindComponents/Button';

export interface AIProcessingStep {
  key: string;
  /** Already-translated label */
  label: string;
  status: 'pending' | 'active' | 'done' | 'error';
  /** Optional already-translated detail line under the label */
  detail?: string;
}

export interface AIProcessingProps {
  /** Already-translated title, e.g. t('Generating draft') */
  title?: string;
  /** Already-translated secondary line */
  subtitle?: string;
  steps?: AIProcessingStep[];
  /** 0-100; renders a thin champagne progress bar when provided */
  progress?: number;
  onCancel?: () => void;
  /** 'page' fills the content region it replaces; 'inline' is compact */
  variant?: 'page' | 'inline';
  className?: string;
}

/**
 * THE AI-process view — replaces the old DraftProcessor/InsightProcessor
 * dark-overlay modals and SSECreationProgress. Renders the brand
 * ring-draw loader on a PLAIN brand background in place of the content
 * it is producing. Never renders a fixed dark overlay.
 */
const AIProcessing: React.FC<AIProcessingProps> = ({
  title,
  subtitle,
  steps,
  progress,
  onCancel,
  variant = 'page',
  className = '',
}) => {
  const { t } = useTranslation();

  const stepIcon = (status: AIProcessingStep['status']) => {
    switch (status) {
      case 'done':
        return <PiCheckCircleLight className="w-4 h-4 text-success flex-shrink-0" aria-hidden="true" />;
      case 'error':
        return <PiWarningCircleLight className="w-4 h-4 text-danger flex-shrink-0" aria-hidden="true" />;
      case 'active':
        return <BrandLoader size="xs" />;
      default:
        return <span className="w-1.5 h-1.5 rounded-full bg-hairline flex-shrink-0" aria-hidden="true" />;
    }
  };

  const body = (
    <>
      <BrandLoader size={variant === 'page' ? 'lg' : 'md'} />
      {title && (
        <h3 className="text-base font-medium text-fg-strong mt-4 text-center">{title}</h3>
      )}
      {subtitle && (
        <p className="text-sm text-fg-muted mt-1 text-center max-w-md">{subtitle}</p>
      )}
      {typeof progress === 'number' && (
        <div
          className="w-full max-w-xs h-0.5 bg-surface-sunken rounded-full mt-4 overflow-hidden"
          role="progressbar"
          aria-valuenow={Math.round(progress)}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div
            className="h-full bg-champagne rounded-full transition-all duration-500 ease-slide"
            style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
          />
        </div>
      )}
      {steps && steps.length > 0 && (
        <ul className="mt-6 space-y-2.5 text-left w-full max-w-sm">
          {steps.map((step) => (
            <li key={step.key} className="flex items-start gap-2.5">
              <span className="mt-0.5 flex items-center justify-center w-4 h-4">
                {stepIcon(step.status)}
              </span>
              <span className="min-w-0">
                <span
                  className={`block text-sm ${
                    step.status === 'active'
                      ? 'text-fg-strong'
                      : step.status === 'pending'
                      ? 'text-fg-muted'
                      : 'text-fg-body'
                  }`}
                >
                  {step.label}
                </span>
                {step.detail && (
                  <span className="block text-xs text-fg-muted mt-0.5">{step.detail}</span>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
      {onCancel && (
        <Button variant="neutral" size="small" onClick={onCancel} className="mt-6">
          {t('Cancel')}
        </Button>
      )}
    </>
  );

  if (variant === 'inline') {
    return (
      <div className={`flex flex-col items-center py-6 bg-bg ${className}`}>{body}</div>
    );
  }

  return (
    <div
      className={`flex flex-col items-center justify-center w-full min-h-[50vh] bg-bg animate-fade-in ${className}`}
    >
      {body}
    </div>
  );
};

export default AIProcessing;
